import { createBrowserRouter, Navigate } from 'react-router-dom';

import { manageWorkersMatomo, matomoTracking } from '@fora-monorepo/webapp-analytics';

import { EmployerMyPagesErrorPage } from '../components/';
import { basePath } from '../config';
import { App } from '../App';
import { ManualPayrollReportingView, PayrollDataFetcher, PayrollHelpView, PayrollStartPage } from '../payroll/views';
import { MarkupsDataFetcher, MarkupsView } from '../markups/views';
import { BaseLayout } from '../common/views';
import { InvoiceDataFetcher, InvoiceHelpView, InvoicesView } from '../invoice/views';
import {
  AddEmployeeView,
  AddEmploymentPeriodView,
  AddEmploymentView,
  EditEmployeeView,
  EditEmploymentPeriodView,
  EditEmploymentView,
  EmploymentsDataFetcher,
  EmploymentsHelpView,
  EmploymentsStartPage,
} from '../employments/views';
import { routePaths } from './paths';
import { FileDashboardDirector, FileDashboardHelpView } from '../fileDashboard/views';
import { ReportHistoryDataFetcher, ReportHistoryView } from '../reportHistory/views';
import { PayHistoryDataFetcher, PayHistoryView } from '../payHistory/views';
import { RequireSelectedOrganization } from './RequireSelectedOrganization';
import { routeHandles } from './routeHandles';
import { RequireFFManageEmployeeAndEmployment } from './RequireFFManageEmployeeAndEmployment';
import { EditParameterView } from '../employments/views/EditParameterView';

export const appRouter = createBrowserRouter(
  [
    {
      path: '/',
      element: <App />,
      children: [
        {
          index: true,
          element: <EmployerMyPagesErrorPage type={404} />,
        },
        {
          element: <BaseLayout />,
          children: [
            {
              path: '*',
              handle: { title: 'errorMessages.404Page.title' },
              element: <EmployerMyPagesErrorPage type={404} />,
            },
            ...routePaths.payroll.map(path => ({
              path,
              handle: routeHandles.payroll,
              element: (
                <RequireSelectedOrganization>
                  <PayrollDataFetcher />
                </RequireSelectedOrganization>
              ),
              children: [
                {
                  index: true,
                  element: <PayrollStartPage />,
                },
                ...routePaths.payrollHelp.map(path => ({
                  path,
                  handle: routeHandles.payrollHelp,
                  element: <PayrollHelpView />,
                })),
                ...routePaths.reportPayroll.map(path => ({
                  path,
                  handle: routeHandles.reportPayroll,
                  element: <ManualPayrollReportingView />,
                })),
                {
                  path: '*',
                  element: <Navigate to={'/' + path} />,
                },
              ],
            })),

            ...routePaths.employments.map(path => ({
              path,
              handle: routeHandles.employments,
              element: (
                <RequireSelectedOrganization>
                  <EmploymentsDataFetcher />
                </RequireSelectedOrganization>
              ),
              children: [
                {
                  index: true,
                  element: <EmploymentsStartPage />,
                  loader: () => {
                    matomoTracking(manageWorkersMatomo, 'start');
                    return null;
                  },
                },
                ...routePaths.editEmployment.map(path => ({
                  path,
                  handle: routeHandles.editEmployment,
                  element: <EditEmploymentView />,
                })),
                ...routePaths.editEmployee.map(path => ({
                  path,
                  handle: routeHandles.editEmployee,
                  element: (
                    <RequireFFManageEmployeeAndEmployment>
                      <EditEmployeeView />
                    </RequireFFManageEmployeeAndEmployment>
                  ),
                })),
                ...routePaths.addEmployee.map(path => ({
                  path,
                  handle: routeHandles.addEmployee,
                  element: (
                    <RequireFFManageEmployeeAndEmployment>
                      <AddEmployeeView />
                    </RequireFFManageEmployeeAndEmployment>
                  ),
                })),
                ...routePaths.editEmploymentPeriod.map(path => ({
                  path,
                  handle: routeHandles.editEmploymentPeriod,
                  element: (
                    <RequireFFManageEmployeeAndEmployment>
                      <EditEmploymentPeriodView />
                    </RequireFFManageEmployeeAndEmployment>
                  ),
                })),
                ...routePaths.addEmploymentPeriod.map(path => ({
                  path,
                  handle: routeHandles.addEmploymentPeriod,
                  element: (
                    <RequireFFManageEmployeeAndEmployment>
                      <AddEmploymentPeriodView />
                    </RequireFFManageEmployeeAndEmployment>
                  ),
                })),
                ...routePaths.editParameter.map(path => ({
                  path,
                  handle: routeHandles.editParameter,
                  element: (
                    <RequireFFManageEmployeeAndEmployment>
                      <EditParameterView />
                    </RequireFFManageEmployeeAndEmployment>
                  ),
                })),
                ...routePaths.addEmployment.map(path => ({
                  path,
                  handle: routeHandles.addEmployment,
                  element: <AddEmploymentView />,
                })),
                ...routePaths.employmentsHelp.map(path => ({
                  path,
                  handle: routeHandles.employmentsHelp,
                  element: <EmploymentsHelpView />,
                })),
                {
                  path: '*',
                  element: <Navigate to={'/' + path} />,
                },
              ],
            })),
            ...routePaths.invoices.map(path => ({
              path,
              handle: routeHandles.invoices,
              element: (
                <RequireSelectedOrganization>
                  <InvoiceDataFetcher />
                </RequireSelectedOrganization>
              ),
              children: [
                {
                  index: true,
                  element: <InvoicesView />,
                },
                ...routePaths.invoicesHelp.map(path => ({
                  path,
                  handle: routeHandles.invoicesHelp,
                  element: <InvoiceHelpView />,
                })),
                {
                  path: '*',
                  element: <Navigate to={'/' + path} />,
                },
              ],
            })),
            ...routePaths.markups.map(path => ({
              path,
              handle: routeHandles.markups,
              element: (
                <RequireSelectedOrganization>
                  <MarkupsDataFetcher />
                </RequireSelectedOrganization>
              ),
              children: [
                {
                  index: true,
                  element: <MarkupsView />,
                },
                {
                  path: '*',
                  element: <Navigate to={'/' + path} />,
                },
              ],
            })),
            ...routePaths.fileDashboard.map(path => ({
              path,
              handle: routeHandles.fileDashboard,
              children: [
                {
                  index: true,
                  element: <FileDashboardDirector />,
                },
                ...routePaths.payHistory.map(path => ({
                  path,
                  handle: routeHandles.payHistory,
                  element: <PayHistoryDataFetcher />,
                  children: [
                    {
                      index: true,
                      element: <PayHistoryView />,
                    },
                    {
                      path: '*',
                      element: <Navigate to={'/' + path} />,
                    },
                  ],
                })),
                ...routePaths.reportHistory.map(path => ({
                  path,
                  handle: routeHandles.reportHistory,
                  element: <ReportHistoryDataFetcher />,
                  children: [
                    {
                      index: true,
                      element: <ReportHistoryView />,
                    },
                    {
                      path: '*',
                      element: <Navigate to={'/' + path} />,
                    },
                  ],
                })),
                ...routePaths.fileDashboardHelp.map(path => ({
                  path,
                  handle: routeHandles.fileDashboardHelp,
                  element: <FileDashboardHelpView />,
                })),
                {
                  path: '*',
                  element: <Navigate to={'/' + path} />,
                },
              ],
            })),
            {
              path: 'login-fora-ks',
              children: [
                {
                  path: ':partnerId',
                  children: [
                    ...routePaths.payroll.map(path => ({
                      path,
                      handle: routeHandles.payroll,
                      element: <Navigate to={'/' + path} />,
                    })),
                    ...routePaths.employments.map(path => ({
                      path,
                      handle: routeHandles.employments,
                      element: <Navigate to={'/' + path} />,
                    })),
                    ...routePaths.invoices.map(path => ({
                      path,
                      handle: routeHandles.invoices,
                      element: <Navigate to={'/' + path} />,
                    })),
                    ...routePaths.markups.map(path => ({
                      path,
                      handle: routeHandles.markups,
                      element: <Navigate to={'/' + path} />,
                    })),
                    ...routePaths.fileDashboard.map(path => ({
                      path,
                      handle: routeHandles.fileDashboard,
                      element: <Navigate to={'/' + path} />,
                    })),
                    {
                      path: ':service',
                      element: <Navigate to={'/' + routePaths.payroll[0]} />,
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  {
    basename: basePath,
  }
);
